import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY } from 'common/constants/services/firms';
import SummaryDefinitionsService from 'services/summaryDefinitions';
import {
  CreateDefinitionPayload,
  SUMMARY_DEFINITIONS_QUERY_KEYS,
  SummaryDefinition,
  SummaryDefinitionListResponse,
  UpdateDefinitionPayload,
  UpdateOrderPayload,
} from './types';

// Append a newly created definition into the cached list immediately, before the
// invalidate/refetch reconciles with the server. This guarantees the list is never briefly
// empty right after a create — which the firm-summary auto-seed effect would otherwise read
// as "no definitions" and use to POST another duplicate default.
const appendDefinitionToCache = (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  created: SummaryDefinition
): void => {
  queryClient.setQueryData<SummaryDefinitionListResponse>(queryKey, prev => {
    if (!prev) {
      return prev;
    }
    if (prev.definitions.some(definition => definition.id === created.id)) {
      return prev;
    }
    return { ...prev, definitions: [...prev.definitions, created] };
  });
};

// ============================================
// Firm Definition Mutations
// ============================================

/**
 * Hook to create a new firm summary definition
 */
export const useCreateFirmDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, CreateDefinitionPayload>({
    mutationFn: data => service.createFirmDefinition(firmId, data),
    onSuccess: created => {
      appendDefinitionToCache(queryClient, SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId), created);
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

/**
 * Hook to update a firm summary definition
 */
export const useUpdateFirmDefinition = (firmId: number, definitionId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, UpdateDefinitionPayload>({
    mutationFn: data => service.updateFirmDefinition(firmId, definitionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
      queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY, firmId, definitionId] });
    },
  });
};

/**
 * Hook to delete a firm summary definition
 */
export const useDeleteFirmDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<void, Error, number>({
    mutationFn: definitionId => service.deleteFirmDefinition(firmId, definitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

/**
 * Hook to update the order of firm summary definition tabs
 */
export const useUpdateFirmDefinitionsOrder = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<{ order: number[] }, Error, UpdateOrderPayload>({
    mutationFn: data => service.updateFirmDefinitionsOrder(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

/**
 * Hook to clone a firm summary definition
 */
export const useCloneFirmDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, { sourceDefinitionId: number; newName: string }>({
    mutationFn: ({ sourceDefinitionId, newName }) => service.cloneFirmDefinition(firmId, sourceDefinitionId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

// ============================================
// Fund Definition Mutations
// ============================================

/**
 * Hook to create a new fund summary definition
 */
export const useCreateFundDefinition = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, CreateDefinitionPayload>({
    mutationFn: data => service.createFundDefinition(fundId, data),
    onSuccess: created => {
      appendDefinitionToCache(queryClient, SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId), created);
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to update a fund summary definition
 */
export const useUpdateFundDefinition = (fundId: number, definitionId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, UpdateDefinitionPayload>({
    mutationFn: data => service.updateFundDefinition(fundId, definitionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to delete a fund summary definition
 */
export const useDeleteFundDefinition = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<void, Error, number>({
    mutationFn: definitionId => service.deleteFundDefinition(fundId, definitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to update the order of fund summary definition tabs
 */
export const useUpdateFundDefinitionsOrder = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<{ order: number[] }, Error, UpdateOrderPayload>({
    mutationFn: data => service.updateFundDefinitionsOrder(fundId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to clone a fund summary definition
 */
export const useCloneFundDefinition = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, { sourceDefinitionId: number; newName: string }>({
    mutationFn: ({ sourceDefinitionId, newName }) => service.cloneFundDefinition(fundId, sourceDefinitionId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

// ============================================
// Firm Valuations Definition Mutations
// ============================================

export const useCreateFirmValuationsDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, CreateDefinitionPayload>({
    mutationFn: data => service.createFirmValuationsDefinition(firmId, data),
    onSuccess: created => {
      appendDefinitionToCache(queryClient, SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(firmId), created);
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(firmId) });
    },
  });
};

export const useUpdateFirmValuationsDefinition = (firmId: number, definitionId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, UpdateDefinitionPayload>({
    mutationFn: data => service.updateFirmValuationsDefinition(firmId, definitionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(firmId) });
    },
  });
};

export const useDeleteFirmValuationsDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<void, Error, number>({
    mutationFn: definitionId => service.deleteFirmValuationsDefinition(firmId, definitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(firmId) });
    },
  });
};

export const useUpdateFirmValuationsDefinitionsOrder = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<{ order: number[] }, Error, UpdateOrderPayload>({
    mutationFn: data => service.updateFirmValuationsDefinitionsOrder(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(firmId) });
    },
  });
};

export const useCloneFirmValuationsDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, { sourceDefinitionId: number; newName: string }>({
    mutationFn: ({ sourceDefinitionId, newName }) =>
      service.cloneFirmValuationsDefinition(firmId, sourceDefinitionId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(firmId) });
    },
  });
};
